import React, { useEffect, useMemo, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Paper, Typography, IconButton, Table, TableCell, TableRow } from '@mui/material';
import {
  FONT_BODY, FONT_DISPLAY, COLORS, TYPE, RADIUS, SHADOW, PAGE_PADDING,
} from '../../styles/recruiterTheme';

// Fonts load once from public/index.html; this only keeps box sizing predictable.
export const ProctoringGlobalStyle = createGlobalStyle`
  * { box-sizing: border-box; }
`;

// ---- Staff user and navigation --------------------------------------------

// location.state first (when a page passes the user along), else the rbotUser saved at login.
export const readStaffUser = (locationState) => {
  if (locationState && locationState.email) return locationState;
  try {
    const parsed = JSON.parse(localStorage.getItem('rbotUser'));
    return parsed && parsed.email ? parsed : null;
  } catch (err) {
    return null;
  }
};

export const DASHBOARD_BY_TYPE = {
  recruiter: '/recruiter-dashboard',
  interviewer: '/interviewer-dashboard',
  hiringManager: '/hiring-dashboard',
  admin: '/admin-dashboard',
  humanResource: '/hr-dashboard',
  manager: '/manager/dashboard',
};

export const dashboardPathFor = (user) => DASHBOARD_BY_TYPE[user && user.userType] || '/login';

// Back goes to the previous page when there is one in this tab, else to the user's dashboard.
export const goBackOrDashboard = (navigate, user) => {
  const hasHistory = typeof window !== 'undefined' && window.history.state && window.history.state.idx > 0;
  if (hasHistory) navigate(-1);
  else navigate(dashboardPathFor(user), { replace: true });
};

// ---- Vocabulary ------------------------------------------------------------

export const REASONS = {
  no_face:         { label: 'No face',         group: 'lost',   bar: '#dc2626', text: '#b91c1c' },
  camera_lost:     { label: 'Camera lost',     group: 'lost',   bar: '#ef4444', text: '#b91c1c' },
  stream_gap:      { label: 'Stream gap',      group: 'lost',   bar: '#f87171', text: '#b91c1c' },
  gaze_off_screen: { label: 'Gaze off screen', group: 'away',   bar: '#f59e0b', text: '#92400e' },
  head_turned:     { label: 'Head turned',     group: 'away',   bar: '#fbbf24', text: '#92400e' },
  pose_unreliable: { label: 'Pose unreliable', group: 'unsure', bar: '#94a3b8', text: '#475569' },
};
export const REASON_ORDER = ['no_face', 'camera_lost', 'stream_gap', 'gaze_off_screen', 'head_turned', 'pose_unreliable'];
export const ON_SCREEN = { label: 'On screen', bar: '#2563eb', text: '#1e40af' };

export const reasonInfo = (reason) =>
  REASONS[reason] || {
    label: reason ? String(reason).replace(/_/g, ' ') : 'Unknown reason',
    group: 'unsure', bar: '#94a3b8', text: '#475569',
  };

export const ROUND_ORDER = ['mcq', 'subjective', 'programming'];
export const ROUND_LABELS = { mcq: 'MCQ', subjective: 'Subjective', programming: 'Programming', unknown: 'Unknown round' };
export const roundLabel = (round) => ROUND_LABELS[round] || (round ? String(round) : 'Unknown round');

export const ENDED_BY_LABELS = {
  'end-session': 'Candidate ended the round',
  'superseded by a new offer': 'Replaced by a new offer',
  'offer failed': 'Camera offer failed',
};
export const endedByLabel = (value) => (value ? (ENDED_BY_LABELS[value] || String(value)) : '');

export const STATUS_STYLES = {
  monitoring: { label: 'Monitoring', bg: '#E6F4EA', fg: '#2D7D46', dot: '#2D7D46' },
  ended:      { label: 'Ended',      bg: '#F1F5F9', fg: '#475569', dot: '#94a3b8' },
  abandoned:  { label: 'Abandoned',  bg: '#FFF8E1', fg: '#E65100', dot: '#F59E0B' },
  superseded: { label: 'Superseded', bg: '#F1F5F9', fg: '#475569', dot: '#94a3b8' },
};

// An open session with no activity for this long is shown as Abandoned.
export const STALE_MS = 3 * 60 * 60 * 1000;
export const MIN_SEGMENT_PCT = 0.35;

// ---- Formatters ------------------------------------------------------------

export const toMs = (value) => {
  if (value === null || value === undefined || value === '') return NaN;
  if (typeof value === 'number') return value;
  if (value && typeof value === 'object' && value.$date) return toMs(value.$date);
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : NaN;
};

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const fmtClock = (secs) => {
  if (!Number.isFinite(secs) || secs < 0) return '00:00';
  const total = Math.floor(secs);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

export const fmtSeconds = (secs) => {
  if (!Number.isFinite(secs) || secs <= 0) return '0 s';
  if (secs < 10) return `${Math.round(secs * 10) / 10} s`;
  const total = Math.round(secs);
  if (total < 60) return `${total} s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return s ? `${m} m ${s} s` : `${m} m`;
};

// "29 Oct 2026 · 11:00"
export const fmtDateTime = (value) => {
  const t = toMs(value);
  if (!Number.isFinite(t)) return 'Unknown';
  const d = new Date(t);
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date} · ${time}`;
};

export const fmtTime = (value) => {
  const t = toMs(value);
  if (!Number.isFinite(t)) return 'Unknown';
  return new Date(t).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

export const questionLabel = (index) => {
  const n = num(index);
  return n === null ? 'Unknown' : `Q${n + 1}`;
};

export const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// ---- Normalisation ---------------------------------------------------------

const sumValues = (obj) => Object.values(obj || {}).reduce((acc, v) => acc + (num(v) || 0), 0);

const perReasonFromEpisodes = (episodes) => {
  const seconds = {};
  const counts = {};
  episodes.forEach((e) => {
    const reason = e && e.reason ? e.reason : 'unknown';
    let dur = num(e.duration_s);
    if (dur === null) {
      const a = toMs(e.start_t);
      const b = toMs(e.end_t);
      dur = Number.isFinite(a) && Number.isFinite(b) ? Math.max(0, (b - a) / 1000) : 0;
    }
    seconds[reason] = (seconds[reason] || 0) + dur;
    counts[reason] = (counts[reason] || 0) + 1;
  });
  return { seconds, counts };
};

const pick = (...values) => {
  for (let i = 0; i < values.length; i += 1) {
    if (values[i] !== undefined && values[i] !== null) return values[i];
  }
  return null;
};

// Turns a raw /api/proctoring session row into the fields the pages render.
// Tolerates rows with or without summary (open rounds) and with or without a joined candidate.
export const normalizeSession = (raw, now = Date.now()) => {
  const s = raw || {};
  const cand = s.candidate || {};
  const violations = cand.proctoringViolations || {};
  const summary = s.summary && typeof s.summary === 'object' ? s.summary : null;
  const episodes = Array.isArray(s.episodes) ? s.episodes.filter(Boolean) : [];

  const startedMs = toMs(s.started_at);
  const endedMs = toMs(s.ended_at);
  const status = s.status === 'ended' || Number.isFinite(endedMs) ? 'ended' : 'open';

  const derived = perReasonFromEpisodes(episodes);
  const seconds = summary && summary.seconds && typeof summary.seconds === 'object' ? summary.seconds : derived.seconds;
  const counts = summary && summary.episodes && typeof summary.episodes === 'object' ? summary.episodes : derived.counts;

  let monitoredS = num(pick(s.monitored_s, summary && summary.monitored_s));
  if (monitoredS === null) {
    if (status === 'open' && Number.isFinite(startedMs)) monitoredS = Math.max(0, (now - startedMs) / 1000);
    else if (Number.isFinite(startedMs) && Number.isFinite(endedMs)) monitoredS = Math.max(0, (endedMs - startedMs) / 1000);
    else monitoredS = 0;
  }

  let episodesCount = num(pick(s.episodes_count, s.episodes_n));
  if (episodesCount === null) episodesCount = episodes.length || sumValues(counts);

  let warnings = num(pick(s.warnings_sent, summary && summary.warnings_sent));
  if (warnings === null) warnings = episodes.reduce((acc, e) => acc + (num(e.reminders) || 0), 0);

  let topReason = s.top_reason || null;
  let topReasonS = num(s.top_reason_s);
  if (!topReason) {
    Object.keys(seconds).forEach((r) => {
      const v = num(seconds[r]) || 0;
      if (v > 0 && (topReasonS === null || v > topReasonS)) { topReason = r; topReasonS = v; }
    });
  }
  if (topReason && topReasonS === null) topReasonS = num(seconds[topReason]);

  const calibration = (summary && summary.calibration) || {};
  const calibrated = pick(s.calibrated, summary && summary.calibrated);
  const override = pick(s.override, calibration.override);

  const lastEpisodeMs = episodes.reduce((acc, e) => {
    const b = toMs(e.end_t);
    return Number.isFinite(b) && b > acc ? b : acc;
  }, NaN);
  const lastActivityMs = [startedMs, toMs(s.last_offer_at), lastEpisodeMs]
    .filter(Number.isFinite)
    .reduce((acc, v) => (v > acc ? v : acc), NaN);
  const stale = status === 'open' && (s.stale === true || (Number.isFinite(lastActivityMs) && now - lastActivityMs > STALE_MS));

  let statusKey = 'monitoring';
  if (status === 'ended') statusKey = s.ended_by === 'superseded by a new offer' ? 'superseded' : 'ended';
  else if (stale) statusKey = 'abandoned';

  return {
    raw: s,
    id: s._id ? String(s._id) : null,
    candidateId: s.candidate_id ? String(s.candidate_id) : (cand._id ? String(cand._id) : null),
    candidatename: pick(s.candidatename, cand.candidatename) || '',
    candidateEmail: pick(s.candidateEmail, cand.candidateEmail) || '',
    round: s.round || 'unknown',
    attempt: num(s.attempt) === null ? 0 : num(s.attempt),
    status,
    statusKey,
    startedMs,
    endedMs,
    endedBy: s.ended_by || '',
    endedByLabel: endedByLabel(s.ended_by),
    monitoredS,
    frames: num(summary && summary.frames),
    episodes,
    episodesCount,
    warnings,
    seconds,
    counts,
    topReason,
    topReasonS,
    calibrated: calibrated === null ? null : Boolean(calibrated),
    override: override === null ? null : Boolean(override),
    calibration,
    engine: (summary && summary.engine && summary.engine.net) || '',
    screen: s.screen || null,
    offers: num(s.offers),
    questionIndexAtOffer: num(s.question_index_at_offer),
    cameraCount: num(pick(s.cameraCount, violations.cameraCount)),
    tabSwitchCount: num(pick(s.tabSwitchCount, violations.count)),
    lastActivityMs,
    stale,
    hasSummary: Boolean(summary),
  };
};

export const groupByAttempt = (sessions) => {
  const map = new Map();
  sessions.forEach((s) => {
    const key = s.attempt;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(s);
  });
  const roundIndex = (r) => { const i = ROUND_ORDER.indexOf(r); return i === -1 ? ROUND_ORDER.length : i; };
  return Array.from(map.entries())
    .sort((a, b) => b[0] - a[0])
    .map(([attempt, list]) => ({
      attempt,
      sessions: list.slice().sort((a, b) => roundIndex(a.round) - roundIndex(b.round)),
    }));
};

// ---- Calibration wording --------------------------------------------------

export const calibrationWord = (s) => {
  if (s.override) return 'Override';
  if (s.calibrated === true) return 'Yes';
  if (s.calibrated === false) return 'No';
  return 'Pending';
};

export const calibrationColor = (s) => {
  const word = calibrationWord(s);
  if (word === 'Yes') return '#2D7D46';
  if (word === 'Override') return '#92400e';
  if (word === 'No') return '#475569';
  return '#8B7DB5';
};

export const calibrationText = (s) => {
  const rms = num(s.calibration && s.calibration.rms_deg);
  const attempts = num(s.calibration && s.calibration.attempts);
  const residual = rms === null ? 'residual unknown' : `residual ${Math.round(rms * 10) / 10} deg`;
  const tries = attempts === null ? '' : `, ${plural(attempts, 'attempt')}`;
  if (s.override) return `Continued after a rejected calibration (${residual}${tries}). Gaze verdicts need a human before they count.`;
  if (s.calibrated === true) return `Calibrated (${residual}${tries}).`;
  if (s.calibrated === false) return `Not calibrated (${residual}${tries}). Gaze verdicts from this round are not calibrated.`;
  return 'Calibration is recorded when the round ends.';
};

// ---- Hooks -----------------------------------------------------------------

export const useNow = (intervalMs = 30000) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const handle = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(handle);
  }, [intervalMs]);
  return now;
};

// ---- Page chrome (recruiter dialect) ---------------------------------------

export const PageWrapper = styled.div`
  font-family: ${FONT_BODY};
  background: ${COLORS.background};
  min-height: 100vh;
  padding: ${PAGE_PADDING}px;
  color: ${COLORS.ink};
  @media (max-width: 700px) { padding: 16px; }
`;

export const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

export const BackBtn = styled(IconButton)`
  && {
    background: ${COLORS.accentSoft};
    border-radius: ${RADIUS.md}px;
    width: 40px; height: 40px;
    flex-shrink: 0;
    transition: background 0.2s;
    &:hover { background: #D0BCFF; }
    svg { color: ${COLORS.primaryDark}; font-size: 22px; }
  }
`;

export const PageTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    letter-spacing: -0.02em;
    line-height: 1.25;
    background: linear-gradient(135deg, ${COLORS.primaryDeep} 0%, ${COLORS.primary} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 4px !important;
  }
`;

export const PageSubtitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.body}px !important;
    color: ${COLORS.muted} !important;
    font-weight: 500 !important;
  }
`;

export const PageCard = styled(Paper)`
  && {
    box-shadow: ${SHADOW.card} !important;
    border: 1px solid ${COLORS.border};
    padding: 24px;
    border-radius: ${RADIUS.lg}px !important;
    background: ${COLORS.surface} !important;
    @media (max-width: 700px) { padding: 16px; }
  }
`;

export const Card = styled(Paper)`
  && {
    border-radius: ${RADIUS.lg}px !important;
    padding: 24px !important;
    box-shadow: ${SHADOW.card} !important;
    border: 1px solid ${COLORS.border};
    background: ${COLORS.surface} !important;
    @media (max-width: 700px) { padding: 16px !important; }
  }
`;

export const CardTitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.cardTitle}px !important;
    color: ${COLORS.primaryDeep};
    margin-bottom: 16px !important;
    padding-bottom: 8px !important;
    border-bottom: 1px solid ${COLORS.border};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
`;

export const CardSub = styled.div`
  font-size: ${TYPE.caption}px;
  color: ${COLORS.muted};
  margin: -8px 0 16px;
`;

export const Muted = styled.span`
  color: ${COLORS.muted};
  font-size: ${TYPE.caption}px;
  font-weight: 500;
`;

export const EmptyNote = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.small}px;
  color: ${COLORS.muted};
  padding: ${(p) => (p.$compact ? '8px 0' : '32px 16px')};
  text-align: ${(p) => (p.$compact ? 'left' : 'center')};
`;

export const StyledTable = styled(Table)`
  && { border-collapse: separate; border-spacing: 0; }
`;

// $dense: tighter horizontal padding for wide tables so they fit a ~1080px content area.
export const HeadCell = styled(TableCell)`
  && {
    background: linear-gradient(135deg, ${COLORS.primaryDark} 0%, ${COLORS.primary} 100%);
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.overline}px !important;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: ${(p) => (p.$dense ? '12px 8px' : '12px 16px')} !important;
    white-space: nowrap;
    border-bottom: none !important;
    &:first-child { border-radius: ${RADIUS.md}px 0 0 ${RADIUS.md}px; }
    &:last-child  { border-radius: 0 ${RADIUS.md}px ${RADIUS.md}px 0; }
  }
`;

export const BodyRow = styled(TableRow)`
  && {
    transition: background 0.16s;
    &:nth-child(even) td { background: #FDFBFF; }
    &:hover td { background: ${COLORS.background} !important; }
    &:focus-visible { outline: 2px solid ${COLORS.primary}; outline-offset: -2px; }
  }
`;

export const BodyCell = styled(TableCell)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.small}px !important;
    font-weight: 500 !important;
    color: ${COLORS.ink} !important;
    padding: ${(p) => (p.$dense ? '12px 8px' : '12px 16px')} !important;
    border-bottom: 1px solid ${COLORS.border} !important;
    vertical-align: middle;
  }
`;

// ---- Pills -----------------------------------------------------------------

export const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border-radius: ${RADIUS.pill}px;
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.caption}px;
  line-height: 1.5;
  background: ${(p) => p.$bg || '#F1F5F9'};
  color: ${(p) => p.$fg || '#475569'};
  white-space: nowrap;
`;

export const PillDot = styled.span`
  width: 7px; height: 7px;
  border-radius: 50%;
  background: ${(p) => p.$color || '#94a3b8'};
  display: inline-block;
  flex-shrink: 0;
`;

export const StatusPill = ({ statusKey, title }) => {
  const style = STATUS_STYLES[statusKey] || STATUS_STYLES.ended;
  return (
    <Pill $bg={style.bg} $fg={style.fg} title={title || undefined}>
      <PillDot $color={style.dot} />
      {style.label}
    </Pill>
  );
};

const tint = (hex) => `${hex}22`;

export const ReasonPill = ({ reason, seconds, count, title }) => {
  const info = reasonInfo(reason);
  const parts = [info.label];
  if (Number.isFinite(seconds) && seconds > 0) parts.push(fmtSeconds(seconds));
  if (Number.isFinite(count) && count > 0) parts.push(plural(count, 'episode'));
  return (
    <Pill $bg={tint(info.bar)} $fg={info.text} title={title || undefined}>
      <PillDot $color={info.bar} />
      {parts.join(' · ')}
    </Pill>
  );
};

// ---- Per-reason bars (plain divs) ------------------------------------------

const BarRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr 96px;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: ${TYPE.caption}px;
`;

const BarLabel = styled.span`
  font-weight: 600;
  color: ${(p) => p.$color || COLORS.ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BarTrack = styled.div`
  height: 10px;
  background: ${COLORS.background};
  border-radius: 5px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 5px;
  transition: width 0.4s ease;
`;

const BarValue = styled.span`
  color: ${COLORS.muted};
  font-weight: 600;
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

export const ReasonBars = ({ session }) => {
  const rows = REASON_ORDER.map((reason) => ({
    reason,
    seconds: num(session.seconds && session.seconds[reason]) || 0,
    count: num(session.counts && session.counts[reason]) || 0,
  }));
  const extra = Object.keys(session.seconds || {}).filter((r) => !REASON_ORDER.includes(r));
  extra.forEach((reason) => rows.push({
    reason,
    seconds: num(session.seconds[reason]) || 0,
    count: num(session.counts && session.counts[reason]) || 0,
  }));
  const max = rows.reduce((acc, r) => (r.seconds > acc ? r.seconds : acc), 0);
  if (max <= 0 && rows.every((r) => r.count === 0)) {
    return <EmptyNote $compact>No episodes in this round.</EmptyNote>;
  }
  return (
    <div>
      {rows.map((r) => {
        const info = reasonInfo(r.reason);
        const width = max > 0 ? Math.max(r.seconds > 0 ? 1 : 0, (r.seconds / max) * 100) : 0;
        return (
          <BarRow key={r.reason}>
            <BarLabel $color={r.seconds > 0 || r.count > 0 ? info.text : '#8B7DB5'}>{info.label}</BarLabel>
            <BarTrack>
              <BarFill style={{ width: `${width}%`, background: info.bar }} />
            </BarTrack>
            <BarValue>{fmtSeconds(r.seconds)} {'·'} {r.count}</BarValue>
          </BarRow>
        );
      })}
    </div>
  );
};

// ---- Episode timeline (plain divs) -----------------------------------------

export const buildTimeline = (session, now = Date.now()) => {
  const t0 = session.startedMs;
  if (!Number.isFinite(t0)) return null;
  const open = !Number.isFinite(session.endedMs);
  const t1 = open ? Math.max(now, t0 + 1000) : session.endedMs;
  const span = Math.max(t1 - t0, 1000);
  const pct = (ms) => Math.min(100, Math.max(0, ((ms - t0) / span) * 100));

  const byReason = {};
  const spans = [];
  session.episodes.forEach((e, i) => {
    const a = toMs(e.start_t);
    if (!Number.isFinite(a)) return;
    let b = toMs(e.end_t);
    if (!Number.isFinite(b)) {
      const dur = num(e.duration_s);
      b = dur === null ? t1 : a + dur * 1000;
    }
    if (b < a) b = a;
    const left = pct(a);
    const right = pct(b);
    const width = Math.max(MIN_SEGMENT_PCT, right - left);
    const seg = {
      key: i,
      left: Math.min(left, 100 - width),
      width,
      reason: e.reason || 'unknown',
      questionIndex: e.question_index,
      durationS: (b - a) / 1000,
      startMs: a,
    };
    if (!byReason[seg.reason]) byReason[seg.reason] = [];
    byReason[seg.reason].push(seg);
    spans.push([left, right]);
  });

  spans.sort((x, y) => x[0] - y[0]);
  const onScreen = [];
  let cursor = 0;
  spans.forEach(([a, b]) => {
    if (a > cursor) onScreen.push({ left: cursor, width: a - cursor });
    if (b > cursor) cursor = b;
  });
  if (cursor < 100) onScreen.push({ left: cursor, width: 100 - cursor });

  const ordered = REASON_ORDER.filter((r) => byReason[r])
    .concat(Object.keys(byReason).filter((r) => !REASON_ORDER.includes(r)));

  return {
    open,
    spanS: span / 1000,
    onScreen,
    rows: ordered.map((reason) => ({ reason, segs: byReason[reason] })),
  };
};

const TimelineWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TrackRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  align-items: center;
  gap: 8px;
`;

const TrackLabel = styled.span`
  font-size: ${TYPE.caption}px;
  font-weight: 600;
  color: ${(p) => p.$color || COLORS.ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TrackBar = styled.div`
  position: relative;
  height: 16px;
  background: ${COLORS.background};
  border-radius: 4px;
  overflow: hidden;
`;

const TrackSeg = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  border-radius: 2px;
`;

const AxisRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 8px;
  font-size: ${TYPE.overline}px;
  color: ${COLORS.muted};
  & > span:last-child { display: flex; justify-content: space-between; }
`;

export const EpisodeTimeline = ({ session, now }) => {
  const model = useMemo(() => buildTimeline(session, now), [session, now]);
  if (!model) {
    return <EmptyNote $compact>Timeline unavailable. This round has no start time.</EmptyNote>;
  }
  const label = roundLabel(session.round);
  return (
    <TimelineWrap>
      <TrackRow>
        <TrackLabel $color={ON_SCREEN.text}>{ON_SCREEN.label}</TrackLabel>
        <TrackBar>
          {model.onScreen.map((seg, i) => (
            <TrackSeg
              key={`on-${i}`}
              style={{ left: `${seg.left}%`, width: `${seg.width}%`, background: ON_SCREEN.bar }}
              title={`On screen · ${fmtSeconds((seg.width / 100) * model.spanS)}`}
            />
          ))}
        </TrackBar>
      </TrackRow>
      {model.rows.map((row) => {
        const info = reasonInfo(row.reason);
        return (
          <TrackRow key={row.reason}>
            <TrackLabel $color={info.text}>{info.label}</TrackLabel>
            <TrackBar>
              {row.segs.map((seg) => (
                <TrackSeg
                  key={seg.key}
                  style={{ left: `${seg.left}%`, width: `${seg.width}%`, background: info.bar }}
                  title={`${label} · ${questionLabel(seg.questionIndex)} · ${info.label} · ${fmtSeconds(seg.durationS)} · ${fmtTime(seg.startMs)}`}
                />
              ))}
            </TrackBar>
          </TrackRow>
        );
      })}
      <AxisRow>
        <span />
        <span>
          <span>start</span>
          <span>{model.open ? 'now' : fmtClock(model.spanS)}</span>
        </span>
      </AxisRow>
      {model.rows.length === 0 && (
        <EmptyNote $compact>No episodes in this round. The whole round was on screen.</EmptyNote>
      )}
    </TimelineWrap>
  );
};
